/**
 * /market/:slug/pagina/:pageSlug — Páginas estáticas del Experience (CMS lite).
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { FileQuestion } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { defaultStaticPages } from "@/lib/experience-definition/presets/helpers";
import { MarketStaticPageView } from "@/components/market/MarketStaticPage";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

export const Route = createFileRoute("/market_/$slug/pagina/$pageSlug")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: StaticPageRoute,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const data = result?.ok ? result.data : null;
    const pages = data?.experiencePublished?.pages ?? defaultStaticPages();
    const page = pages.find((p) => p.enabled && p.slug === params.pageSlug);
    return {
      meta: [{ title: page ? `${page.title} — ${data?.tenant.name ?? params.slug}` : params.pageSlug }],
      links: [{ rel: "manifest", href: `/api/manifest/${params.slug}` }],
    };
  },
});

function StaticPageRoute() {
  const { slug, pageSlug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <FileQuestion className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{result.error}</p>
      </div>
    );
  }

  const data = result.data;
  const pages = data.experiencePublished?.pages ?? defaultStaticPages();
  const page = pages.find((p) => p.enabled && p.slug === pageSlug);
  if (!page) throw notFound();

  return <MarketStaticPageView slug={slug} data={data} page={page} />;
}
